/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  Grid,
  Autocomplete,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { useSelector } from "react-redux";
import Loading from "../../../../common/loader/loading";
import NotificationSnackbar from "../../../../common/notificationBar/notification-snackbar";
import ManagerDefaultTaskCard from "./manager-default-task-card";
import CreateEditDialog from "../../../../common/dialog/create-edit-dialog";

const ManagerDefaultTaskAssign = () => {
  const user = useSelector((state) => state.auth.user);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null); // Track the selected task
  const [timelineFilter, setTimelineFilter] = useState("all"); // Track selected timeline filter
  const [filteredTasks, setFilteredTasks] = useState([]); // Tasks filtered by timeline
  const [openCreateSnackbar, setCreateOpenSnackbar] = useState(false);
  const [openEditSnackbar, setEditOpenSnackbar] = useState(false);
  const [openDeleteSnackbar, setDeleteOpenSnackbar] = useState(false);
  const [users, setUsers] = useState([]);
  const [newAssignee, setNewAssignee] = useState(selectedTask?.assignee || "");
  const [newTaskStatus, setNewTaskStatus] = useState(selectedTask?.taskStatus || "");
  const [witnessId, setWitnessId] = useState("");
  const [changeAssignee, setChangeAssignee] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [staffDebriefs, setstaffDebriefs] = useState([]);
  const [childDebriefs, setChildDebriefs] = useState([]);
  const [newShifType, setNewShiftType] = useState(selectedTask?.shiftType);
  const [roomSearches, setRoomSearches] = useState([]);
  const [openProf, setProfOpen] = useState(false);

  useEffect(() => {
    const fetchRoomSearch = async () => {
      try {
        const response = await axios.get("/api/room/roomSearchAll");

        // If witnessId is set, filter the response
        if (user?.id) {
          const filteredSearches = response.data.filter(
            (search) => search.witnessId === Number(user?.id) && search.status === "pending"
          );
          setRoomSearches(filteredSearches);
        } else {
          setRoomSearches(response.data);
        }
      } catch (error) {
        console.error("Error fetching child debriefs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRoomSearch();
  }, [user?.id, refresh]); // Re-fetch when user ID changes

  useEffect(() => {
    const fetchStaffDebriefs = async () => {
      try {
        const response = await axios.get("/api/staff/staffDebriefAll");

        // If witnessId is set, filter the response
        if (user?.id) {
          const filteredDebriefs = response.data.filter(
            (debrief) => debrief.witnessId === Number(user?.id) && debrief.status === "pending"
          );
          setstaffDebriefs(filteredDebriefs);
        } else {
          setstaffDebriefs(response.data);
        }
      } catch (error) {
        console.error("Error fetching child debriefs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStaffDebriefs();
  }, [user?.id, refresh]); // Re-fetch when user ID changes

  useEffect(() => {
    const fetchChildDebriefs = async () => {
      try {
        const response = await axios.get("/api/child/childDebriefAll");

        // If witnessId is set, filter the response
        if (user?.id) {
          const filteredDebriefs = response.data.filter(
            (debrief) => debrief.witnessId === Number(user?.id) && debrief.status === "pending"
          );
          setChildDebriefs(filteredDebriefs);
        } else {
          setChildDebriefs(response.data);
        }
      } catch (error) {
        console.error("Error fetching child debriefs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchChildDebriefs();
  }, [user?.id, refresh]); // Re-fetch when user ID changes

  // Handle role selection (Involved/Witness)
  const handleTaskStatus = (event, taskStatus) => {
    if (taskStatus !== null) {
      setNewTaskStatus(taskStatus);
      setChangeAssignee(true);
    }
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/assessments/assetAll", {
        headers: {
          "x-user-role": user?.roleid, // Custom header
        },
      });
      // Filter tasks where status is 'pending'
      const pendingTasks = response.data.filter((task) => task.status === "pending");
      setTasks(pendingTasks);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [refresh]);

  // Filter tasks based on the selected timeline
  useEffect(() => {
    if (timelineFilter === "all") {
      setFilteredTasks(tasks);
    } else {
      setFilteredTasks(tasks.filter((task) => task.shiftType === timelineFilter));
    }
  }, [tasks, timelineFilter]);

  const handleFilterChange = (filter) => {
    setTimelineFilter(filter);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Make the API request
        const response = await axios.get("/api/auth/users");
        setUsers(response.data);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    setNewAssignee(selectedTask?.assignee || ""); // Default to an empty string if undefined
    setNewTaskStatus(selectedTask?.taskStatus || "");
    setNewShiftType(selectedTask?.shiftType || "");
  }, [selectedTask]); // Runs when selectedTask changes

  const handleSubmit = async () => {
    setRefresh(true); // Set refresh state before making the requests

    try {
      if (
        !selectedTask?.taskName?.includes("Staff") &&
        !selectedTask?.taskName?.includes("Child") &&
        !selectedTask?.taskName?.includes("Room")
      ) {
        // Perform the first API request to update the assessment task
        await axios.put(
          `/api/assessments/asset/${selectedTask?.id}`,
          {
            taskName: selectedTask?.taskName || "",
            description: selectedTask?.description || "",
            timeline: selectedTask?.timeline || "",
            checklist: selectedTask?.checklist?.map((item) => ({
              item: item.item || "",
              status: false,
            })) || [{ item: "", status: false }],
            status: "pending",
            shiftType: newTaskStatus === "inactive" ? "unassigned" : newShifType,
            managed_by: user?.username,
            taskType: selectedTask?.taskType || "manual",
            assignee: newTaskStatus === "inactive" ? "unassigned" : newAssignee,
            taskStatus: newAssignee && newAssignee !== "unassigned" ? "active" : newTaskStatus,
            taskOwner: selectedTask?.taskOwner,
          },
          {
            headers: {
              "Content-Type": "application/json",
              "x-user-role": user?.roleid,
            },
          }
        );
      }

      if (selectedTask?.taskName?.includes("Staff")) {
        // Perform the second API request to update the staff debrief
        await axios.put(
          `/api/staff/staffDebrief/${selectedTask?.id}`,
          {
            witnessId: witnessId,
            taskName: selectedTask?.taskName,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      }
      if (selectedTask?.taskName?.includes("Child")) {
        // Perform the second API request to update the staff debrief
        await axios.put(
          `/api/child/childDebrief/${selectedTask?.id}`,
          {
            witnessId: witnessId,
            taskName: selectedTask?.taskName,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      }
      if (selectedTask?.taskName?.includes("Room")) {
        // Perform the second API request to update the staff debrief
        await axios.put(
          `/api/room/roomSearch/${selectedTask?.id}`,
          {
            witnessId: witnessId,
            taskName: selectedTask?.taskName,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      }
      setChangeAssignee(false); // Reset changeAssignee state
      setProfOpen(false); // Close the dialog
    } catch (error) {
      console.error("Error updating task or support statement:", error);
      alert("Failed to update. Please try again.");
    }
    setRefresh(false); // Set refresh state before making the requests
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Grid container className='task-container'>
      <Grid item sm={8} className='task-list'>
        <Box sx={{ p: 4, ml: 2, mr: 1 }}>
          <Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "black" }} align='left'>
            {"Tasks | Manage Tasks"}
          </Typography>
          <Grid container spacing={2}>
            <Grid item lg={10} xs={12}>
              <Box sx={{ mb: 4, display: "flex", gap: 3 }}>
                <Button
                  variant='outlined'
                  color='black'
                  onClick={() => handleFilterChange("all")}
                  sx={{ textTransform: "none", borderColor: timelineFilter === "all" ? "#1976d2" : "#ECECED" }}
                >
                  <span
                    style={{
                      backgroundColor: timelineFilter === "all" ? "#1976d2" : "#DCDCDE",
                      color: timelineFilter === "all" ? "white" : "black",
                      padding: "0 4px",
                      marginRight: "10px",
                      borderRadius: "4px",
                    }}
                  >
                    {tasks.length}
                  </span>{" "}
                  All
                </Button>
                <Button
                  variant='outlined'
                  color='black'
                  onClick={() => handleFilterChange("Day Shift")}
                  sx={{ textTransform: "none", borderColor: timelineFilter === "Day Shift" ? "#1976d2" : "#ECECED" }}
                >
                  <span
                    style={{
                      backgroundColor: timelineFilter === "Day Shift" ? "#1976d2" : "#DCDCDE",
                      color: timelineFilter === "Day Shift" ? "white" : "black",
                      padding: "0 4px",
                      marginRight: "10px",
                      borderRadius: "4px",
                    }}
                  >
                    {tasks.filter((task) => task.shiftType === "Day Shift").length}
                  </span>{" "}
                  Day
                </Button>
                <Button
                  variant='outlined'
                  color='black'
                  onClick={() => handleFilterChange("Night Shift")}
                  sx={{ textTransform: "none", borderColor: timelineFilter === "Night Shift" ? "#1976d2" : "#ECECED" }}
                >
                  <span
                    style={{
                      backgroundColor: timelineFilter === "Night Shift" ? "#1976d2" : "#DCDCDE",
                      color: timelineFilter === "Night Shift" ? "white" : "black",
                      padding: "0 4px",
                      marginRight: "10px",
                      borderRadius: "4px",
                    }}
                  >
                    {tasks.filter((task) => task.shiftType === "Night Shift").length}
                  </span>{" "}
                  Night
                </Button>
                <Button
                  variant='outlined'
                  color='black'
                  onClick={() => handleFilterChange("Sleep In")}
                  sx={{ textTransform: "none", borderColor: timelineFilter === "Sleep In" ? "#1976d2" : "#ECECED" }}
                >
                  <span
                    style={{
                      backgroundColor: timelineFilter === "Sleep In" ? "#1976d2" : "#DCDCDE",
                      color: timelineFilter === "Sleep In" ? "white" : "black",
                      padding: "0 4px",
                      marginRight: "10px",
                      borderRadius: "4px",
                    }}
                  >
                    {tasks.filter((task) => task.shiftType === "Sleep In").length}
                  </span>{" "}
                  Sleeping
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Box className='scrollable-task-list'>
            <ManagerDefaultTaskCard
              staffDebriefs={staffDebriefs}
              roomSearches={roomSearches}
              childDebriefs={childDebriefs}
              tasks={filteredTasks}
              onTaskClick={setSelectedTask}
              refreshTasks={fetchTasks}
              setEditOpenSnackbar={setEditOpenSnackbar}
              setDeleteOpenSnackbar={setDeleteOpenSnackbar}
            />
          </Box>
        </Box>
        {/* Notification Snackbar for Create */}
        <NotificationSnackbar
          open={openCreateSnackbar}
          onClose={() => setCreateOpenSnackbar(false)}
          color='success'
          message='Task Created Successfully. Task added to Support Worker dashboard.'
        />
        {/* Notification Snackbar for Edit */}
        <NotificationSnackbar
          open={openEditSnackbar}
          onClose={() => setEditOpenSnackbar(false)}
          color='warning'
          message='Task Edited Successfully. Task Updated to Support Worker dashboard.'
        />
        {/* Notification Snackbar for Delete */}
        <NotificationSnackbar
          open={openDeleteSnackbar}
          onClose={() => setDeleteOpenSnackbar(false)}
          color='error'
          message='Task Deleted Successfully. Task deleted from Support Worker dashboard.'
        />
      </Grid>
      <Grid item sm={4}>
        <Box className='detail-view'>
          {selectedTask && (
            <Box sx={{ p: 2, ml: 1, mr: 2 }}>
              {/* Task Title */}
              <Typography sx={{ fontWeight: "bold", fontSize: "16px", color: "black", mb: 1 }}>
                {selectedTask?.taskName}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "black", mb: 1 }}>{selectedTask?.description}</Typography>
              <Grid container alignItems='center' sx={{ mb: 2 }}>
                <Grid item lg={6} md={6} sm={12}>
                  <Typography sx={{ fontSize: "14px" }}>Status</Typography>
                </Grid>
                <Grid item lg={6} md={6} sm={12}>
                  {selectedTask?.status}
                </Grid>
              </Grid>
              {!selectedTask.taskName.includes("Staff") &&
                !selectedTask.taskName.includes("Child") &&
                !selectedTask.taskName.includes("Room") && (
                  <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                    <Grid item lg={6} md={6} sm={12}>
                      <Typography sx={{ fontSize: "14px" }}>Shift</Typography>
                    </Grid>
                    <Grid item lg={6} md={6} sm={12}>
                      {selectedTask?.shiftType || "unassigned"}
                    </Grid>
                  </Grid>
                )}
              {/* Assignee */}
              <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <Grid item lg={6} md={6} sm={12}>
                  <Typography sx={{ fontSize: "14px" }}> Assigned To</Typography>
                </Grid>
                <Grid item lg={6} md={6} sm={12}>
                  {selectedTask?.assignee || "unassigned"}
                </Grid>
              </Grid>
              {selectedTask?.managed_by !== "unassigned" ||
                (selectedTask?.assignee !== "unassigned" && (
                  <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                    <Grid item lg={6} md={6} sm={12}>
                      <Typography sx={{ fontSize: "14px" }}> Assigned By</Typography>
                    </Grid>
                    <Grid item lg={6} md={6} sm={12}>
                      {selectedTask?.managed_by}
                    </Grid>
                  </Grid>
                ))}

              {selectedTask?.status !== "completed" && (
                <Grid container spacing={1} className='submit-button-container'>
                  <Grid item>
                    <Button
                      type='submit'
                      variant='contained'
                      color='primary'
                      className='submit-button'
                      sx={{ textTransform: "none" }}
                      onClick={() => {
                        setProfOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </Box>
        <CreateEditDialog isOpen={openProf} onClose={() => setProfOpen(false)}>
          <Box>
            {selectedTask && (
              <Box sx={{ p: 2, ml: 1, mr: 2 }}>
                {/* Task Title */}
                <Typography sx={{ fontWeight: "bold", fontSize: "16px", color: "black", mb: 1 }}>
                  {selectedTask?.taskName}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "black", mb: 1 }}>{selectedTask?.description}</Typography>
                <Grid container alignItems='center' sx={{ mb: 2 }}>
                  <Grid item lg={6} md={6} sm={12}>
                    <Typography sx={{ fontSize: "14px" }}>Status</Typography>
                  </Grid>
                  <Grid item lg={6} md={6} sm={12}>
                    <RadioGroup
                      row
                      value={newTaskStatus}
                      onChange={handleTaskStatus}
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <FormControlLabel
                        value='active'
                        control={<Radio size='small' />}
                        label='Active'
                        sx={{
                          margin: 0,
                          "& .MuiTypography-root": { fontSize: "14px" },
                        }}
                      />
                      <FormControlLabel
                        value='inactive'
                        control={<Radio size='small' />}
                        label='Inactive'
                        sx={{
                          margin: 0,
                          "& .MuiTypography-root": { fontSize: "14px" },
                        }}
                      />
                    </RadioGroup>
                  </Grid>
                </Grid>
                {/* Assignee */}
                <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                  <Grid item lg={6} md={6} sm={12}>
                    <Typography sx={{ fontSize: "14px" }}> Assigned To</Typography>
                  </Grid>
                  <Grid item lg={6} md={6} sm={12}>
                    {(selectedTask.taskName.includes("Child") ||
                      selectedTask.taskName.includes("Staff") ||
                      selectedTask.taskName.includes("Room")) && (
                      <Autocomplete
                        id='assignee'
                        options={[{ id: null, username: "unassigned" }, ...(users || [])]} // Prepend 'Unassigned'
                        getOptionLabel={(option) => option?.username || ""}
                        value={newAssignee}
                        onChange={(event, newValue) => {
                          setWitnessId(newValue?.id || null);
                          setChangeAssignee(true);
                        }}
                        renderInput={(params) => <TextField {...params} placeholder='Select' fullWidth />}
                        fullWidth
                      />
                    )}
                    {!selectedTask.taskName.includes("Staff") &&
                      !selectedTask.taskName.includes("Child") &&
                      !selectedTask.taskName.includes("Room") && (
                        <Autocomplete
                          id='assignee'
                          options={["unassigned", ...(users?.map((item) => item?.username) || [])]}
                          value={newAssignee || "Unassigned"}
                          onChange={(event, newValue) => {
                            setNewAssignee(newValue === "Unassigned" ? null : newValue);
                            setChangeAssignee(true);
                          }}
                          renderInput={(params) => <TextField {...params} placeholder='Select' fullWidth />}
                          fullWidth
                        />
                      )}
                  </Grid>
                </Grid>
                {!selectedTask.taskName.includes("Staff") &&
                  !selectedTask.taskName.includes("Child") &&
                  !selectedTask.taskName.includes("Room") && (
                    <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                      <Grid item lg={6} md={6} sm={12}>
                        <Typography sx={{ fontSize: "14px" }}>Shift Type</Typography>
                      </Grid>
                      <Grid item lg={6} md={6} sm={12}>
                        <Autocomplete
                          id='shiftType'
                          options={["Day Shift", "Night Shift", "Sleep In"]}
                          value={newShifType}
                          onChange={(event, newValue) => {
                            setNewShiftType(newValue);
                            setChangeAssignee(true);
                          }}
                          renderInput={(params) => (
                            <>
                              <TextField {...params} placeholder='Select' />
                            </>
                          )}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  )}

                {changeAssignee && newShifType && (
                  <Grid container spacing={1} className='submit-button-container'>
                    <Grid item>
                      <Button
                        type='submit'
                        variant='contained'
                        color='primary'
                        className='submit-button'
                        sx={{ textTransform: "none" }}
                        onClick={handleSubmit}
                      >
                        Update
                      </Button>
                    </Grid>
                  </Grid>
                )}
                {changeAssignee &&
                  (selectedTask.taskName.includes("Child") ||
                    selectedTask.taskName.includes("Staff") ||
                    selectedTask.taskName.includes("Room")) && (
                    <Grid container spacing={1} className='submit-button-container'>
                      <Grid item>
                        <Button
                          type='submit'
                          variant='contained'
                          color='primary'
                          className='submit-button'
                          sx={{ textTransform: "none" }}
                          onClick={handleSubmit}
                        >
                          Update
                        </Button>
                      </Grid>
                    </Grid>
                  )}
              </Box>
            )}
          </Box>
        </CreateEditDialog>
      </Grid>
    </Grid>
  );
};

export default ManagerDefaultTaskAssign;
